'use client';
import Loader from '@susu/custom-made/components/loader/loader';
import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import { startAnimation } from '@susu/custom-made/utils/animation';
import { Level, ZoomPosition } from '@susu/custom-made/utils/constants';
import { Section } from '@susu/custom-made/utils/navigation';
import type { LayerDefinition } from '@susu/custom-made/utils/preview/layers';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { productCodeByConfigurationId, ProductMandatoryLayerSize } from '@susu/custom-made/utils/productDefinition';
import { PREVIEW_TEST_IDS } from '@susu/custom-made/utils/testIds';
import {
  checkConfigurationTypeRotationAvailable,
  checkRotationStep,
  getMEASProductTypeByConfigurationType,
} from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { TransformComponent, useControls } from 'react-zoom-pan-pinch';

import Cursor from '../cursor/cursor';
import PreviewFirstLayer from './previewFirstLayer';
import PreviewOtherLayers from './previewOtherLayers';
import { ZoomableContainer } from './zoomableContainer';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useGlobalContext from '@susu/custom-made/providers/hooks/useGlobalContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const DOUBLE_CLICK_DISABLED = { disabled: true };

const PREVIEW_CONTAINER =
  'h-full w-full relative [&>div:not(.cursorContainer)]:h-full! [&>div:not(.cursorContainer)]:w-full! [&>div:not(.cursorContainer)>div]:h-full! [&>div:not(.cursorContainer)>div]:w-full!';

const getPreviewClasses = (isZoomable?: boolean) =>
  cn(
    'bg-gray-300 w-full relative',
    isZoomable ? 'mt-32 mb-8 h-[calc(100%-40px)] lg:mt-40 lg:h-[calc(100%-48px)]' : 'h-full',
  );

const ZOOM_PANEL_TOP_4 =
  'top-[calc(((100dvh-188px-var(--webstore-header-height))/4)-50px)] md:top-[calc(((100dvh-244px-var(--webstore-header-height))/4)-50px)] lg:top-[calc(((100dvh-var(--webstore-header-height))/4)-50px)]';
const ZOOM_PANEL_TOP_2 =
  'top-[calc(((100dvh-188px-var(--webstore-header-height))/2)-50px)] md:top-[calc(((100dvh-244px-var(--webstore-header-height))/2)-50px)] lg:top-[calc(((100dvh-var(--webstore-header-height))/2)-50px)]';
const ZOOM_PANEL_BOTTOM_4 =
  'bottom-[calc(((100dvh-188px-var(--webstore-header-height))/4)-50px)] md:bottom-[calc(((100dvh-244px-var(--webstore-header-height))/4)-50px)] lg:bottom-[calc(((100dvh-var(--webstore-header-height))/4)-50px)]';
const ZOOM_PANEL_LEFT_4 =
  'left-[calc((100dvw/4)-50px)] lg:left-[calc((60dvw/4)-50px)]';
const ZOOM_PANEL_LEFT_2 =
  'left-[calc((100dvw/2)-50px)] lg:left-[calc((60dvw/2)-50px)]';
const ZOOM_PANEL_RIGHT_4 =
  'right-[calc((100dvw/4)-50px)] lg:right-[calc((60dvw/4)-50px)]';

const ZOOM_PANEL_POSITIONS: Record<string, string> = {
  TopLeft: `${ZOOM_PANEL_TOP_4} ${ZOOM_PANEL_LEFT_4}`,
  TopCenter: `${ZOOM_PANEL_TOP_4} ${ZOOM_PANEL_LEFT_2}`,
  TopRight: `${ZOOM_PANEL_TOP_4} ${ZOOM_PANEL_RIGHT_4}`,
  MiddleLeft: `${ZOOM_PANEL_TOP_2} ${ZOOM_PANEL_LEFT_4}`,
  MiddleCenter: `${ZOOM_PANEL_TOP_2} ${ZOOM_PANEL_LEFT_2}`,
  MiddleRight: `${ZOOM_PANEL_TOP_2} ${ZOOM_PANEL_RIGHT_4}`,
  BottomLeft: `${ZOOM_PANEL_BOTTOM_4} ${ZOOM_PANEL_LEFT_4}`,
  BottomCenter: `${ZOOM_PANEL_BOTTOM_4} ${ZOOM_PANEL_LEFT_2}`,
  BottomRight: `${ZOOM_PANEL_BOTTOM_4} ${ZOOM_PANEL_RIGHT_4}`,
};

interface PreviewControlsProps {
  isPreviewZoomed: boolean;
  isPreviewPanning: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onPositionButtonRef: (position: number, ref: HTMLButtonElement | null) => void;
  zoomButtonRef: React.RefObject<HTMLButtonElement>;
  resetButtonRef: React.RefObject<HTMLButtonElement>;
  presetIndex: number;
}

function PreviewControls({
  isPreviewZoomed,
  isPreviewPanning,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onPositionButtonRef,
  zoomButtonRef,
  resetButtonRef,
  presetIndex,
}: Readonly<PreviewControlsProps>) {
  const { zoomIn, resetTransform, zoomToElement } = useControls();
  const viewSize = useViewSize();

  const zoomInPreview = () => {
    if (isPreviewZoomed && (viewSize === ViewSize.Mobile || viewSize === ViewSize.Tablet || !isPreviewPanning)) {
      resetTransform();
      onZoomOut();
    } else if (!isPreviewZoomed) {
      zoomIn();
      onZoomIn();
    }
  };

  const zoomElement = (id: string) => {
    zoomToElement(id, 1.65);
    onZoomIn();
  };

  const resetZoom = () => {
    resetTransform();
    onResetZoom();
  };

  return (
    <>
      <button className="hidden" onClick={zoomInPreview} ref={zoomButtonRef}>
        Zoom
      </button>
      <button className="hidden" onClick={resetZoom} ref={resetButtonRef}>
        Reset
      </button>
      {Object.values(ZoomPosition).map(position => (
        <button
          key={`button_${position}`}
          className="hidden"
          ref={ref => onPositionButtonRef(position, ref)}
          onClick={() => {
            zoomElement(`zoomPanel_${presetIndex}_${position}`);
          }}
        ></button>
      ))}
    </>
  );
}

const { SHOW_3D_PREVIEW_IMAGE, ENABLE_ROTATION_ANIMATION } = FEATURE_TOGGLES;

export interface PreviewProps {
  presetIndex?: number;
  layers: LayerDefinition[];
  fallbackImage: LayerDefinition;
  isZoomable?: boolean;
  configurationType?: ConfigurationTypes;
  isPreviewLoaderActive?: boolean;
}

function Preview({
  presetIndex = 0,
  layers,
  fallbackImage,
  isZoomable,
  configurationType,
  isPreviewLoaderActive = false,
}: Readonly<PreviewProps>) {
  const { section, level } = useNavigationContext();
  const { isPreviewPanning, isPreviewZoomed, setIsPreviewPanning, setIsPreviewZoomed } = useGlobalContext();
  const { animation, selectedOption, setAnimation } = useOptionContext();
  const [isPreviewFailed, setIsPreviewFailed] = useState(false);

  // Reset only when the base address changes: a failed base <img> keeps the fallback set on the DOM, and
  // React does not restore an unchanged src, so a reset on an equal list would draw details over the fallback.
  const baseLayerSrc = layers[0]?.src;
  useEffect(() => {
    setIsPreviewFailed(false);
  }, [baseLayerSrc]);

  const zoomButton = useRef<HTMLButtonElement>(null);
  const resetButton = useRef<HTMLButtonElement>(null);
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [showCursor, setShowCursor] = useState(false);
  const zoomButtonInputRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const previewContainer = useRef<HTMLDivElement>(null);
  const [isLoaderActive, setIsLoaderActive] = useState(false);
  const { selectedFabric } = useFabricContext();
  const [layersDone, setLayersDone] = useState<string[]>(
    configurationType
      ? Array.from(
          {
            length:
              ProductMandatoryLayerSize[
                productCodeByConfigurationId[+configurationType] as keyof typeof ProductMandatoryLayerSize
              ],
          },
          () => 'done',
        )
      : [],
  );
  const mandatoryLayerSize = useMemo(() => {
    if (configurationType) {
      return ProductMandatoryLayerSize[
        productCodeByConfigurationId[configurationType] as keyof typeof ProductMandatoryLayerSize
      ];
    }

    return 0;
  }, [configurationType]);
  const show3DPreviewImageFeatureEnabled = useFeatureIsOn(SHOW_3D_PREVIEW_IMAGE);
  const enableRotationAnimationFeatureEnabled = useFeatureIsOn(ENABLE_ROTATION_ANIMATION);

  // Synchronous panning flag. isPreviewPanning is deliberately delayed by 100ms
  // (for other consumers), but we need an immediate signal here so that the
  // synthetic click browsers fire after touchend never reaches zoomButton when
  // the user was just panning.
  //
  // Crucially this is set in onPanning (movement detected), NOT in onPanningStart
  // (touch-down), so that stationary taps still trigger zoom normally.
  const isPanningRef = useRef(false);

  const clickToZoom = () => {
    if (!isPanningRef.current) {
      zoomButton.current?.click();
    }
  };

  const clickToReset = useCallback(() => {
    resetButton.current?.click();
  }, []);

  // Stable config for ZoomableContainer; memoized by hand as the Shopify embed runs without the React Compiler.
  const panningStart = useCallback(() => {
    setTimeout(() => {
      setIsPreviewPanning(true);
    }, 100);
  }, [setIsPreviewPanning]);

  const onPanning = useCallback(() => {
    isPanningRef.current = true;
  }, []);

  const panningStop = useCallback(() => {
    setTimeout(() => {
      isPanningRef.current = false;
      setIsPreviewPanning(false);
    }, 100);
  }, [setIsPreviewPanning]);

  const dispatchZoomIn = useCallback(() => {
    setIsPreviewZoomed(true);
  }, [setIsPreviewZoomed]);

  const dispatchZoomOut = useCallback(() => {
    setIsPreviewZoomed(false);
  }, [setIsPreviewZoomed]);

  const checkScaleState = useCallback(
    (ref: ReactZoomPanPinchRef) => {
      if (ref.state.scale === 1) {
        dispatchZoomOut();
      }
    },
    [dispatchZoomOut],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mouseMove = (e: any) => {
    setMouseY(e.clientY + 15);
    setMouseX(e.clientX + 10);
  };

  const onAnimationEnd = () => {
    setAnimation({ status: false, from: undefined, to: undefined });
  };

  useEffect(() => {
    if (animation.status && show3DPreviewImageFeatureEnabled) {
      if (enableRotationAnimationFeatureEnabled) {
        if (checkConfigurationTypeRotationAvailable(configurationType, show3DPreviewImageFeatureEnabled)) {
          const element = previewContainer.current;
          startAnimation({
            element,
            fromStep: checkRotationStep(animation.from).spriteStep,
            onAnimationEnd,
            toStep: checkRotationStep(animation.to).spriteStep,
            totalSteps: 19,
          });
        }
      } else {
        onAnimationEnd();
      }
    }
  }, [animation.status]);

  useEffect(() => {
    if (section) {
      clickToReset();
    }
  }, [section, clickToReset]);

  useEffect(() => {
    if (
      selectedOption?.zoomPosition &&
      selectedOption?.zoomPosition > 0 &&
      level === Level.Subgroup &&
      section !== Section.Fabric
    ) {
      zoomButtonInputRefs.current[selectedOption?.zoomPosition]?.click();
    } else {
      clickToReset();
    }
  }, [selectedOption, clickToReset, level, section]);

  useEffect(() => {
    if (!isPreviewLoaderActive) return;

    setLayersDone([]);
    setIsLoaderActive(true);
    const timer = setTimeout(() => {
      setIsLoaderActive(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [selectedFabric, isPreviewLoaderActive]);

  return (
    <div className={PREVIEW_CONTAINER} data-testid={PREVIEW_TEST_IDS.LAYERS_PREVIEW} ref={previewContainer}>
      {isZoomable && (
        <ZoomableContainer
          doubleClick={DOUBLE_CLICK_DISABLED}
          onPanningStart={panningStart}
          onPanning={onPanning}
          onPanningStop={panningStop}
          disablePadding={true}
          onWheelStart={dispatchZoomIn}
          onPinchingStart={dispatchZoomIn}
          onWheelStop={checkScaleState}
          onPinchingStop={checkScaleState}
          maxScale={2}
        >
          <PreviewControls
            isPreviewZoomed={isPreviewZoomed}
            isPreviewPanning={isPreviewPanning}
            onPositionButtonRef={(position, ref) => {
              zoomButtonInputRefs.current[position] = ref;
            }}
            onResetZoom={() => setIsPreviewZoomed(false)}
            onZoomIn={dispatchZoomIn}
            onZoomOut={dispatchZoomOut}
            presetIndex={presetIndex}
            resetButtonRef={resetButton}
            zoomButtonRef={zoomButton}
          />
          <div className="cursorContainer">
            <Cursor showCursor={showCursor} isZoomed={isPreviewZoomed} mouseX={mouseX} mouseY={mouseY} />
          </div>
          <TransformComponent>
            {Object.keys(ZoomPosition).map(position => (
              <div
                key={`div_${position}`}
                id={`zoomPanel_${presetIndex}_${ZoomPosition[position]}`}
                className={cn('absolute w-[100px] h-[100px]', ZOOM_PANEL_POSITIONS[position])}
              ></div>
            ))}
            <div
              className={getPreviewClasses(true)}
              data-testid={`${PREVIEW_TEST_IDS.LAYERS_PREVIEW}-${getMEASProductTypeByConfigurationType(configurationType!).toLocaleLowerCase()}`}
              onClick={clickToZoom}
              onMouseMove={mouseMove}
              onMouseOver={() => {
                setShowCursor(true);
              }}
              onMouseOut={() => {
                setShowCursor(false);
              }}
            >
              <div className={layersDone.length >= mandatoryLayerSize || !isLoaderActive ? 'block' : 'hidden'}>
                <PreviewFirstLayer
                  layers={layers}
                  fallbackImage={fallbackImage}
                  isPreviewFailed={isPreviewFailed}
                  setIsPreviewFailed={setIsPreviewFailed}
                  configurationType={configurationType}
                  setLayersDone={setLayersDone}
                />
                {!isPreviewFailed && (
                  <PreviewOtherLayers
                    layers={layers}
                    configurationType={configurationType}
                    setLayersDone={setLayersDone}
                  />
                )}
              </div>
              {layersDone.length < mandatoryLayerSize && isLoaderActive && isPreviewLoaderActive && (
                <Loader isTransparentBackground={true} isLightLoader={true} />
              )}
            </div>
          </TransformComponent>
        </ZoomableContainer>
      )}
      {!isZoomable && (
        <div className={getPreviewClasses()} data-testid={`${PREVIEW_TEST_IDS.LAYERS_PREVIEW}-${presetIndex}`}>
          <PreviewFirstLayer
            layers={layers}
            fallbackImage={fallbackImage}
            isPreviewFailed={isPreviewFailed}
            setIsPreviewFailed={setIsPreviewFailed}
            configurationType={configurationType}
          />
          {!isPreviewFailed && (
            <PreviewOtherLayers layers={layers} configurationType={configurationType} setLayersDone={setLayersDone} />
          )}
        </div>
      )}
    </div>
  );
}

export default memo(Preview);
